
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { FieldError } from "@/components/properties/FieldError";

interface PropertyFinancialFormProps {
  price: string;
  arv: string;
  zillowLink: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  errors?: Record<string, string>;
  clearFieldError?: (field: string) => void;
}

export const PropertyFinancialForm = ({
  price,
  arv,
  zillowLink,
  onChange,
  errors = {},
  clearFieldError,
}: PropertyFinancialFormProps) => {
  const errCls = (field: string) =>
    errors[field] ? "border-destructive focus-visible:ring-destructive" : "";

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Financial Details</h2>
          <Separator />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="price">Asking Price*</Label>
              <Input
                id="price"
                name="price"
                value={price}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  const formatted = value ? `$${parseInt(value).toLocaleString()}` : '';
                  e.target.value = formatted;
                  onChange(e);
                  clearFieldError?.("price");
                }}
                required
                aria-invalid={!!errors.price}
                className={cn("bg-muted", errCls("price"))}
              />
              <FieldError message={errors.price} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="arv">After Repair Value (ARV)</Label>
              <Input
                id="arv"
                name="arv"
                value={arv}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  const formatted = value ? `$${parseInt(value).toLocaleString()}` : '';
                  e.target.value = formatted;
                  onChange(e);
                  clearFieldError?.("arv");
                }}
                aria-invalid={!!errors.arv}
                className={cn("bg-muted", errCls("arv"))}
              />
              <FieldError message={errors.arv} />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="zillowLink">Zillow Link (Optional)</Label>
              <Input
                id="zillowLink"
                name="zillowLink"
                value={zillowLink}
                onChange={onChange}
                className="bg-muted"
              />
              <p className="text-xs text-muted-foreground">
                Link is auto-generated, input is optional. Contract expiration date is managed in the Property Status tab.
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
